import { useCallback } from 'react';
import { Film, Copy, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

function clone(obj) {
  if (obj == null) return {};
  try {
    return JSON.parse(JSON.stringify(obj));
  } catch {
    return {};
  }
}

function setDeep(root, path, value) {
  const out = clone(root);
  let cur = out;
  for (let i = 0; i < path.length - 1; i++) {
    const p = path[i];
    const nextKey = path[i + 1];
    if (cur[p] === undefined) cur[p] = typeof nextKey === 'number' ? [] : {};
    cur = cur[p];
  }
  cur[path[path.length - 1]] = value;
  return out;
}

const Field = ({ label, value, onChange, multiline = false, className = '' }) => (
  <div className={className}>
    <Label className="text-xs font-medium text-[#64748B] mb-1 block">{label}</Label>
    {multiline ? (
      <Textarea
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-[72px] rounded-xl border-slate-200 text-sm"
      />
    ) : (
      <Input
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 rounded-xl border-slate-200 text-sm"
      />
    )}
  </div>
);

export function CinematicBlueprintEditor({ movie, onChange }) {
  const m = movie && typeof movie === 'object' ? movie : {};

  const patch = useCallback(
    (path, value) => {
      onChange(setDeep(m, path, value));
    },
    [m, onChange],
  );

  const copyJson = () => {
    try {
      void navigator.clipboard.writeText(JSON.stringify({ movie: m }, null, 2));
      toast.success('Blueprint JSON copied');
    } catch {
      toast.error('Could not copy');
    }
  };

  const overall = m.overallDescription || {};
  const world = m.worldDesign || {};
  const styleGuide = m.styleGuide || {};
  const segments = Array.isArray(m.segments) ? m.segments : [];
  const characters = Array.isArray(m.characters) ? m.characters : [];
  const audioMaster = m.audioMasterGuide || {};
  const masterPrompts = m.masterPrompts || {};
  const post = m.postProduction || {};

  return (
    <div className="space-y-4" data-testid="cinematic-blueprint-editor">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-sm font-bold text-[#1E293B]">
          <Film className="h-4 w-4 text-[#6366F1]" />
          Cinematic blueprint (editable)
        </div>
        <Button type="button" variant="outline" size="sm" className="rounded-lg gap-1 h-8" onClick={copyJson}>
          <Copy className="h-3.5 w-3.5" /> Copy JSON
        </Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3 rounded-2xl border border-slate-100 bg-white p-4">
        <Field label="Movie title" value={m.title} onChange={(v) => patch(['title'], v)} />
        <Field
          label="Genres (comma-separated)"
          value={Array.isArray(m.genre) ? m.genre.join(', ') : m.genre || ''}
          onChange={(v) =>
            patch(
              ['genre'],
              v
                .split(',')
                .map((s) => s.trim())
                .filter(Boolean),
            )
          }
        />
        <Field label="Target audience" value={m.targetAudience} onChange={(v) => patch(['targetAudience'], v)} />
        <Field label="Duration" value={m.duration} onChange={(v) => patch(['duration'], v)} />
      </div>

      <details className="rounded-2xl border border-slate-100 bg-white open:shadow-sm">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          Overall movie description
        </summary>
        <div className="space-y-3 border-t border-slate-50 p-4 pt-4">
          <Field label="Summary" value={overall.summary} onChange={(v) => patch(['overallDescription', 'summary'], v)} multiline />
          <Field label="Emotional core" value={overall.emotionalCore} onChange={(v) => patch(['overallDescription', 'emotionalCore'], v)} multiline />
          <Field label="Tone" value={overall.tone} onChange={(v) => patch(['overallDescription', 'tone'], v)} />
          <Field label="Visual identity" value={overall.visualIdentity} onChange={(v) => patch(['overallDescription', 'visualIdentity'], v)} multiline />
          <Field label="Color language" value={overall.colorLanguage} onChange={(v) => patch(['overallDescription', 'colorLanguage'], v)} multiline />
        </div>
      </details>

      <details className="rounded-2xl border border-slate-100 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          World / environment design
        </summary>
        <div className="space-y-3 border-t border-slate-50 p-4 pt-4">
          <Field label="Era" value={world.era} onChange={(v) => patch(['worldDesign', 'era'], v)} />
          <Field label="Geography" value={world.geography} onChange={(v) => patch(['worldDesign', 'geography'], v)} multiline />
          <Field label="Architecture" value={world.architecture} onChange={(v) => patch(['worldDesign', 'architecture'], v)} multiline />
          <Field label="Atmosphere" value={world.atmosphere} onChange={(v) => patch(['worldDesign', 'atmosphere'], v)} multiline />
        </div>
      </details>

      <details className="rounded-2xl border border-slate-100 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          Visual style guide
        </summary>
        <div className="grid sm:grid-cols-2 gap-3 border-t border-slate-50 p-4 pt-4">
          <Field label="Style type" value={styleGuide.styleType} onChange={(v) => patch(['styleGuide', 'styleType'], v)} />
          <Field label="Realism level" value={styleGuide.realismLevel} onChange={(v) => patch(['styleGuide', 'realismLevel'], v)} />
          <Field label="Camera style" value={styleGuide.cameraStyle} onChange={(v) => patch(['styleGuide', 'cameraStyle'], v)} />
          <Field label="Color grading" value={styleGuide.colorGrading} onChange={(v) => patch(['styleGuide', 'colorGrading'], v)} />
        </div>
      </details>

      <div className="rounded-2xl border border-slate-100 bg-white p-4">
        <Label className="text-sm font-semibold text-[#1E293B] mb-3 block">Characters</Label>
        <div className="space-y-4">
          {characters.map((ch, ci) => (
            <details key={ch.id || ci} className="rounded-xl border border-slate-100 bg-slate-50/50">
              <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-[#334155]">
                {ch.name || `Character ${ci + 1}`}
              </summary>
              <div className="space-y-2 p-3 pt-1">
                <Field label="Name" value={ch.name} onChange={(v) => patch(['characters', ci, 'name'], v)} />
                <Field label="Role" value={ch.role} onChange={(v) => patch(['characters', ci, 'role'], v)} />
                <Field
                  label="Face consistency prompt"
                  value={ch.consistencyPrompts?.facePrompt}
                  onChange={(v) => patch(['characters', ci, 'consistencyPrompts', 'facePrompt'], v)}
                  multiline
                />
                <Field
                  label="Full body prompt"
                  value={ch.consistencyPrompts?.fullBodyPrompt}
                  onChange={(v) => patch(['characters', ci, 'consistencyPrompts', 'fullBodyPrompt'], v)}
                  multiline
                />
              </div>
            </details>
          ))}
          {characters.length === 0 ? <p className="text-xs text-[#94A3B8]">No characters in blueprint yet.</p> : null}
        </div>
      </div>

      <div className="space-y-3">
        <Label className="text-sm font-semibold text-[#1E293B] block">Segments &amp; scenes</Label>
        {segments.map((seg, si) => {
          const scenes = Array.isArray(seg.scenes) ? seg.scenes : [];
          return (
            <details key={seg.segmentId || si} className="rounded-2xl border border-indigo-100 bg-[#FAFBFF] open:bg-white">
              <summary className="cursor-pointer px-4 py-3 text-sm font-semibold text-[#4338CA]">
                Segment {si + 1}: {seg.title || 'Untitled segment'}
              </summary>
              <div className="space-y-3 border-t border-indigo-50 p-4">
                <Field label="Segment title" value={seg.title} onChange={(v) => patch(['segments', si, 'title'], v)} />
                <Field label="Purpose" value={seg.purpose} onChange={(v) => patch(['segments', si, 'purpose'], v)} multiline />
                <Field label="Emotional tone" value={seg.emotionalTone} onChange={(v) => patch(['segments', si, 'emotionalTone'], v)} />
                <Field
                  label="Background — location"
                  value={seg.background?.location}
                  onChange={(v) => patch(['segments', si, 'background', 'location'], v)}
                  multiline
                />

                <div className="space-y-2 pt-2">
                  <span className="text-xs font-semibold uppercase tracking-wide text-[#64748B]">Scenes</span>
                  {scenes.map((sc, qi) => {
                    const vp = sc.videoPrompt || {};
                    const img = sc.imagePrompts || {};
                    const dlines = Array.isArray(sc.dialogues) ? sc.dialogues : [];
                    return (
                      <details key={sc.sceneId || qi} className="rounded-xl border border-slate-200 bg-white">
                        <summary className="cursor-pointer px-3 py-2 text-xs font-semibold text-[#1E293B]">
                          Scene {qi + 1}: {sc.sceneTitle || sc.sceneId || 'Scene'}
                        </summary>
                        <div className="space-y-2 border-t border-slate-100 p-3">
                          <Field
                            label="Scene summary"
                            value={sc.sceneSummary}
                            onChange={(v) => patch(['segments', si, 'scenes', qi, 'sceneSummary'], v)}
                            multiline
                          />
                          <Field
                            label="Main video prompt"
                            value={vp.mainPrompt}
                            onChange={(v) => patch(['segments', si, 'scenes', qi, 'videoPrompt', 'mainPrompt'], v)}
                            multiline
                          />
                          <Field
                            label="Environment"
                            value={vp.environment}
                            onChange={(v) => patch(['segments', si, 'scenes', qi, 'videoPrompt', 'environment'], v)}
                            multiline
                          />
                          <Field
                            label="Camera"
                            value={vp.camera}
                            onChange={(v) => patch(['segments', si, 'scenes', qi, 'videoPrompt', 'camera'], v)}
                            multiline
                          />
                          <div className="grid sm:grid-cols-2 gap-2">
                            <Field
                              label="Thumbnail image prompt"
                              value={img.thumbnailPrompt}
                              onChange={(v) => patch(['segments', si, 'scenes', qi, 'imagePrompts', 'thumbnailPrompt'], v)}
                              multiline
                            />
                            <Field
                              label="Storyboard image prompt"
                              value={img.storyboardPrompt}
                              onChange={(v) => patch(['segments', si, 'scenes', qi, 'imagePrompts', 'storyboardPrompt'], v)}
                              multiline
                            />
                          </div>

                          <div className="rounded-lg border border-slate-100 bg-slate-50/80 p-2">
                            <span className="text-[11px] font-semibold text-[#64748B]">Dialogue lines</span>
                            <div className="mt-2 space-y-2">
                              {dlines.map((d, di) => (
                                <div key={di} className="flex flex-col gap-1 rounded-lg border border-white bg-white p-2 text-xs">
                                  <div className="flex gap-2">
                                    <Input
                                      placeholder="Character"
                                      value={d.character ?? ''}
                                      onChange={(e) =>
                                        patch(['segments', si, 'scenes', qi, 'dialogues', di, 'character'], e.target.value)
                                      }
                                      className="h-8 text-xs"
                                    />
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 shrink-0 text-red-600"
                                      onClick={() => {
                                        const next = dlines.filter((_, j) => j !== di);
                                        patch(['segments', si, 'scenes', qi, 'dialogues'], next);
                                      }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  </div>
                                  <Textarea
                                    placeholder="Line"
                                    value={d.dialogue ?? ''}
                                    onChange={(e) =>
                                      patch(['segments', si, 'scenes', qi, 'dialogues', di, 'dialogue'], e.target.value)
                                    }
                                    className="min-h-[48px] text-xs"
                                  />
                                </div>
                              ))}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-xs"
                                onClick={() =>
                                  patch(['segments', si, 'scenes', qi, 'dialogues'], [
                                    ...dlines,
                                    { character: '', dialogue: '', subtext: '', emotion: '', deliveryStyle: '' },
                                  ])
                                }
                              >
                                <Plus className="h-3.5 w-3.5 mr-1" /> Add dialogue line
                              </Button>
                            </div>
                          </div>

                          <Field
                            label="Narration text"
                            value={sc.narration?.text}
                            onChange={(v) => patch(['segments', si, 'scenes', qi, 'narration', 'text'], v)}
                            multiline
                          />

                          <div className="grid sm:grid-cols-2 gap-2">
                            <Field
                              label="Camera — movement"
                              value={sc.cameraDirection?.movement}
                              onChange={(v) => patch(['segments', si, 'scenes', qi, 'cameraDirection', 'movement'], v)}
                              multiline
                            />
                            <Field
                              label="Lighting — key"
                              value={sc.lighting?.keyLight}
                              onChange={(v) => patch(['segments', si, 'scenes', qi, 'lighting', 'keyLight'], v)}
                              multiline
                            />
                          </div>
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            </details>
          );
        })}
        {segments.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">No segments yet—generate the blueprint first.</p>
        ) : null}
      </div>

      <details className="rounded-2xl border border-slate-100 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          Audio master guide
        </summary>
        <div className="space-y-3 border-t border-slate-50 p-4">
          <Field
            label="Soundtrack style"
            value={audioMaster.soundtrackStyle}
            onChange={(v) => patch(['audioMasterGuide', 'soundtrackStyle'], v)}
            multiline
          />
          <Field label="Vocal style" value={audioMaster.vocalStyle} onChange={(v) => patch(['audioMasterGuide', 'vocalStyle'], v)} multiline />
        </div>
      </details>

      <details className="rounded-2xl border border-slate-100 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          Master prompts (lists — one item per line)
        </summary>
        <div className="space-y-3 border-t border-slate-50 p-4">
          <Field
            label="Character consistency"
            value={Array.isArray(masterPrompts.characterConsistency) ? masterPrompts.characterConsistency.join('\n') : ''}
            onChange={(v) =>
              patch(
                ['masterPrompts', 'characterConsistency'],
                v.split('\n').map((s) => s.trim()).filter(Boolean),
              )
            }
            multiline
          />
          <Field
            label="Video generation"
            value={Array.isArray(masterPrompts.videoGeneration) ? masterPrompts.videoGeneration.join('\n') : ''}
            onChange={(v) =>
              patch(
                ['masterPrompts', 'videoGeneration'],
                v.split('\n').map((s) => s.trim()).filter(Boolean),
              )
            }
            multiline
          />
        </div>
      </details>

      <details className="rounded-2xl border border-slate-100 bg-white">
        <summary className="cursor-pointer select-none px-4 py-3 text-sm font-semibold text-[#1E293B]">
          Post-production
        </summary>
        <div className="grid sm:grid-cols-2 gap-3 border-t border-slate-50 p-4">
          <Field label="Color grading" value={post.colorGrading} onChange={(v) => patch(['postProduction', 'colorGrading'], v)} multiline />
          <Field label="LUT style" value={post.lutStyle} onChange={(v) => patch(['postProduction', 'lutStyle'], v)} />
          <Field label="VFX style" value={post.vfxStyle} onChange={(v) => patch(['postProduction', 'vfxStyle'], v)} multiline />
        </div>
      </details>
    </div>
  );
}
